// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later

import { loadMeta } from '../data';
import { openOverlay } from './drawer';
import { esc, num, shortDate } from '../format';
import { GLOSSARY } from '../glossary';

export async function openAbout(): Promise<void> {
  const meta = await loadMeta().catch(() => null);
  const gates = meta?.gates ?? [];
  const passed = gates.filter((g) => g.pass).length;

  openOverlay('modal', `
    <h2>About this site</h2>
    <p>Petrol in most Australian cities does not drift up and down with the oil price — it saw-tooths.
    Prices jump on one day of the week and then drift down until the next jump. Knowing which day
    that is, is worth real money, and the answer has changed more than once.</p>

    <h3>What it shows</h3>
    <p>Every price reported by every service station in Western Australia since 3 January 2001, and in
    Queensland since December 2018 — ${num(meta?.counts.waObservations ?? 0)} Western Australian daily
    readings and ${num(meta?.counts.qldEvents ?? 0)} Queensland price changes, from
    ${num(meta?.counts.waMonths ?? 0)} and ${num(meta?.counts.qldMonths ?? 0)} monthly files respectively.</p>

    <h3>How the cycle is measured, and why it matters</h3>
    <p>The obvious method — take the median price for each day of the week across a month — is wrong,
    and wrong in a way that looks completely convincing. If prices fall all month, the later days of the
    week come out cheaper simply because they happened later, and the chart will confidently name a day
    that was not cheapest in any individual week. In March 2026 that method hid 73% of Perth's real cycle.</p>
    <p>Instead, every statistic here is computed one complete Monday-to-Sunday week at a time, and the
    cycle is identified by the <strong>hike</strong> — the day the price jumped, found from the change
    between consecutive days. The hike is 20 to 45 cents; the underlying drift is about one cent a day,
    so the hike survives any trend. The day before the hike is the day to fill up.</p>

    <h3>What it will not tell you</h3>
    <ul>
      <li><strong>Only two states.</strong> Western Australia and Queensland publish complete open price
      histories. New South Wales publishes one too, but under a share-alike licence that cannot be mixed
      into the same derived files, so it is deliberately excluded rather than quietly bent. Victoria, South
      Australia, Tasmania, the ACT and the Northern Territory publish no comparable open history.</li>
      <li><strong>No cheapest-day advice outside Perth and Peel.</strong> Brisbane's cycle runs about
      36 days, not seven, so no day of the week is reliably cheap there. In remote regions the price
      barely moves at all. Both are stated as such rather than given a number that would look precise
      and be meaningless.</li>
      <li><strong>Prices are nominal.</strong> The 2001 figures are not adjusted for inflation.</li>
      <li><strong>Queensland stations report only when their price changes</strong>, so a Queensland
      figure is the last price that station notified, carried forward. A price is never carried more than
      60 days, because a station that has gone quiet for two months is closed rather than cheap.</li>
      <li><strong>Coordinates come from the live registers</strong>, so stations that closed before today
      cannot be placed on the map. They still appear in every table.</li>
      ${(meta?.qldMissingMonths ?? []).length ? `<li><strong>Queensland has a gap.</strong>
      ${esc((meta!.qldMissingMonths as string[]).join(', '))} could not be retrieved — those monthly
      files are published but do not open. The Queensland charts break the line across that period
      rather than drawing through it, because a gap shown as a straight line is worse than a gap
      shown as a gap.</li>` : ''}
    </ul>

    <h3>Where the data comes from</h3>
    <ul>${(meta?.sources ?? []).map((s) => `<li><a href="${esc(s.url)}" target="_blank" rel="noopener">${esc(s.name)}</a> — ${esc(s.licence)}, updated ${esc(s.cadence.toLowerCase())}</li>`).join('')}</ul>
    <p>Latest Western Australian data ${esc(shortDate(meta?.latest.WA ?? ''))}; latest Queensland data
    ${esc(shortDate(meta?.latest.QLD ?? ''))}. The pipeline runs monthly, which is the rate Queensland
    publishes at.</p>

    <h3>Checks that run before anything is published</h3>
    <p><strong>${passed} of ${gates.length} passed</strong> on this build. They include checks designed to
    fail rather than merely pass: that the naive method still gets the cycle wrong where it is known to,
    and that remote regions whose prices do not move are never handed a confident "cheapest day".</p>
    <div class="table-scroll" style="margin-top:var(--space-sm)">
      <table><tbody>${gates.map((g) => `<tr>
        <td style="color:${g.pass ? 'var(--status-good)' : 'var(--status-bad)'};font-weight:650">${g.pass ? 'PASS' : 'FAIL'}</td>
        <td>${esc(g.name)}<br><small style="color:var(--text-tertiary)">${esc(g.detail)}</small></td>
      </tr>`).join('')}</tbody></table>
    </div>

    <h3>Words used here</h3>
    <div class="table-scroll" style="margin-top:var(--space-sm)">
      <table><tbody>${Object.values(GLOSSARY).map((t) => `<tr>
        <td style="white-space:nowrap;vertical-align:top;font-weight:600">${esc(t.term)}</td>
        <td>${esc(t.definition)}</td>
      </tr>`).join('')}</tbody></table>
    </div>
  `, 'About this site');
}
